import { useEffect, useRef } from "react";
import { anchorName, triggerWords, type Touch } from "./emailSmsTypes";

export type Anchor = { x: number; y: number };

export type TouchDiff = {
  /** Added on this campaign, so there is no catalog original behind it. */
  isAdded: boolean;
  triggerChanged: boolean;
  copyEdited: boolean;
  /** The catalog moved on after this campaign edited the touch. */
  catalogUpdated: boolean;
};

export function diffOf(touch: Touch, original: Touch | undefined): TouchDiff {
  if (!original) {
    return { isAdded: true, triggerChanged: false, copyEdited: false, catalogUpdated: false };
  }
  return {
    isAdded: false,
    triggerChanged:
      touch.days !== original.days ||
      touch.dir !== original.dir ||
      touch.anchor !== original.anchor,
    copyEdited: !!touch.customized,
    catalogUpdated: !!touch.customized && (original.ver ?? 1) > (touch.ver ?? 1),
  };
}

export const isEdited = (diff: TouchDiff) =>
  diff.isAdded || diff.triggerChanged || diff.copyEdited;

/** Added steps have no catalog default, so nothing to reset to. */
export const canReset = (diff: TouchDiff) =>
  !diff.isAdded && (diff.triggerChanged || diff.copyEdited);

function useDismiss(onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  return ref;
}

type MenuProps = {
  at: Anchor;
  touch: Touch;
  diff: TouchDiff;
  onPreview: () => void;
  onEdit: () => void;
  onSendTest: () => void;
  onReset: () => void;
  onDelete: () => void;
  onClose: () => void;
};

export function TouchRowMenu({
  at,
  touch,
  diff,
  onPreview,
  onEdit,
  onSendTest,
  onReset,
  onDelete,
  onClose,
}: MenuProps) {
  const ref = useDismiss(onClose);
  const run = (fn: () => void) => () => {
    onClose();
    fn();
  };

  return (
    <div
      className="touch-menu"
      role="menu"
      aria-label={`Actions for ${touch.title}`}
      ref={ref}
      // Right-aligned to the button, which sits at the row's end — a left-aligned menu
      // would hang off the pane.
      style={{ left: at.x, top: at.y, transform: "translateX(-100%)" }}
    >
      <button type="button" role="menuitem" onClick={run(onPreview)}>
        <i className="bi bi-eye" />
        Preview
      </button>
      <button type="button" role="menuitem" onClick={run(onSendTest)}>
        <i className="bi bi-send" aria-hidden="true" />
        Send test
      </button>
      <button type="button" role="menuitem" onClick={run(onEdit)}>
        <i className="bi bi-pencil" />
        Edit copy
      </button>
      {canReset(diff) && (
        <button type="button" role="menuitem" onClick={run(onReset)}>
          <i className="bi bi-arrow-counterclockwise" />
          Reset to default
        </button>
      )}
      {diff.isAdded && (
        <button
          type="button"
          role="menuitem"
          className="touch-menu__danger"
          onClick={run(onDelete)}
        >
          <i className="bi bi-trash" />
          Delete step
        </button>
      )}
    </div>
  );
}

type CustProps = {
  at: Anchor;
  touch: Touch;
  original?: Touch;
  diff: TouchDiff;
  onReset: () => void;
  onViewChanges: () => void;
  onClose: () => void;
};

/*
    One "Customized" dot replaced four separate affordances — Changed from default, Reset,
    Catalog updated and View changes. This popover is where all four went, so what differs
    from the catalog is read in one place rather than inferred from badges.
*/
export function CustomizedPopover({
  at,
  touch,
  original,
  diff,
  onReset,
  onViewChanges,
  onClose,
}: CustProps) {
  const ref = useDismiss(onClose);

  return (
    <div
      className="touch-cust"
      role="dialog"
      aria-label="Customized"
      ref={ref}
      style={{ left: at.x, top: at.y }}
    >
      <div className="touch-cust__title">Customized for this campaign</div>

      {diff.isAdded && (
        <div className="touch-cust__line">
          Added on this campaign. Not in the catalog playbook.
        </div>
      )}
      {diff.triggerChanged && original && (
        <div className="touch-cust__line">
          Trigger: {triggerWords(touch.days, touch.dir, anchorName(touch.anchor))}. Default:{" "}
          {triggerWords(original.days, original.dir, anchorName(original.anchor))}.
        </div>
      )}
      {diff.copyEdited && !diff.isAdded && (
        <div className="touch-cust__line">Copy edited for this campaign.</div>
      )}
      {diff.catalogUpdated && (
        <div className="touch-cust__line touch-cust__line--warn">
          ! The catalog changed this message after it was edited here.
        </div>
      )}

      <div className="touch-cust__foot">
        {canReset(diff) && (
          <button type="button" className="esms__link" onClick={onReset}>
            Reset to default
          </button>
        )}
        {diff.catalogUpdated && (
          <button type="button" className="esms__link" onClick={onViewChanges}>
            View catalog changes
          </button>
        )}
        {diff.isAdded && (
          <span className="touch-cust__note">No catalog default to reset to.</span>
        )}
      </div>
    </div>
  );
}
